import { ref, computed, onUnmounted } from 'vue'
import { messengerApi } from '@/features/messenger/api/messenger.api'

export type CallState = 'idle' | 'initiating' | 'ringing' | 'accepted' | 'connected' | 'terminated'

const callState = ref<CallState>('idle')
const callId = ref<string | null>(null)
const remoteJid = ref<string | null>(null)
const callDirection = ref<'outgoing' | 'incoming' | null>(null)
const callStartedAt = ref<Date | null>(null)
const callAnsweredAt = ref<Date | null>(null)
const callEndedAt = ref<Date | null>(null)
const callReason = ref<string>('')
const callError = ref<string>('')
const durationSeconds = ref(0)
const isMuted = ref(false)

let durationTimer: ReturnType<typeof setInterval> | null = null

function startDurationTimer() {
  stopDurationTimer()
  durationTimer = setInterval(() => {
    if (callAnsweredAt.value) {
      durationSeconds.value = Math.floor((Date.now() - callAnsweredAt.value.getTime()) / 1000)
    }
  }, 1000)
}

function stopDurationTimer() {
  if (durationTimer) {
    clearInterval(durationTimer)
    durationTimer = null
  }
}

function resetCall() {
  callState.value = 'idle'
  callId.value = null
  remoteJid.value = null
  callDirection.value = null
  callStartedAt.value = null
  callAnsweredAt.value = null
  callEndedAt.value = null
  callReason.value = ''
  callError.value = ''
  durationSeconds.value = 0
  isMuted.value = false
  stopDurationTimer()
}

export function useWhatsAppCall() {
  const isActive = computed(() => callState.value !== 'idle' && callState.value !== 'terminated')

  const formattedDuration = computed(() => {
    const mins = Math.floor(durationSeconds.value / 60)
    const secs = durationSeconds.value % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  })

  async function initiateCall(phone: string) {
    if (isActive.value) {
      callError.value = 'Another call is already active'
      return
    }

    callError.value = ''
    callState.value = 'initiating'
    remoteJid.value = `${phone}@s.whatsapp.net`
    callDirection.value = 'outgoing'
    callStartedAt.value = new Date()

    try {
      const resp = await messengerApi.initiateCall(phone)
      callId.value = resp.callId
      callState.value = 'ringing'
    } catch (e) {
      callError.value = String(e)
      callState.value = 'terminated'
      callReason.value = 'error'
    }
  }

  async function terminateCall() {
    if (!callId.value) return

    try {
      await messengerApi.terminateCall(callId.value)
    } catch (e) {
      callError.value = String(e)
    }
    callState.value = 'terminated'
    callEndedAt.value = new Date()
    callReason.value = 'local_hangup'
    stopDurationTimer()
  }

  function toggleMute() {
    isMuted.value = !isMuted.value
  }

  // Handle WebSocket call state updates
  function handleCallStateEvent(data: {
    call_id: string
    state: string
    remote_jid: string
    direction?: string
    started_at?: string
    answered_at?: string
    ended_at?: string
    reason?: string
  }) {
    // Only process events for our active call or new incoming calls
    if (callId.value && data.call_id !== callId.value) return

    if (!callId.value) {
      callId.value = data.call_id
    }
    remoteJid.value = data.remote_jid

    const newState = data.state as CallState
    callState.value = newState

    if (data.answered_at && !callAnsweredAt.value) {
      callAnsweredAt.value = new Date(data.answered_at)
      startDurationTimer()
    }

    if (data.ended_at) {
      callEndedAt.value = new Date(data.ended_at)
      stopDurationTimer()
    }

    if (data.reason) {
      callReason.value = data.reason
    }

    // Auto-transition connected state starts timer
    if (newState === 'connected' && !callAnsweredAt.value) {
      callAnsweredAt.value = new Date()
      startDurationTimer()
    }

    if (newState === 'terminated') {
      stopDurationTimer()
    }
  }

  onUnmounted(() => {
    stopDurationTimer()
  })

  return {
    callState,
    callId,
    remoteJid,
    callDirection,
    callStartedAt,
    callAnsweredAt,
    callEndedAt,
    callReason,
    callError,
    durationSeconds,
    formattedDuration,
    isMuted,
    isActive,
    initiateCall,
    terminateCall,
    toggleMute,
    resetCall,
    handleCallStateEvent,
  }
}
